import React, { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { useHashScroll, usePageMeta, useSiteMotion } from "./useSiteMotion";
import { signedIn } from "./session";
import founder from "../Assets/founder-dancers.jpg";
import feisLogo from "../Assets/feis-logo.svg";
import { EmailIcon, FacebookIcon, InstagramIcon, MailboxIcon } from "./SocialIcons";
import "./site.css";

// /about: who makes Feis.link, what it stands for and how to reach us.
// No figures row here (Devin 2026-10-16); the organiser page carries them.

// The headline opens with the logo itself in place of the word Feis.link,
// with "by Devin Cornell" in green (Devin 2026-10-16).
const HEADLINE = [["by", "g"], ["Devin", "g"], ["Cornell", "g"]];

const BELIEFS = [
  ["The feis keeps its money", "Organisers pay nothing, and Feis.link pays the credit card fees, so the full entry price on the syllabus lands in the feis's own account."],
  ["Feis day runs on one system", "From the first entry to the last award: registration, payments, tabulation and results, all in one place your committee can run."],
  ["Results families can read", "Every judge's placing, laid out so families can follow it at a glance, published to their phones the moment each competition is done. Judges' comments go privately to the dancer's family."],
];

const About = () => {
  const root = useRef(null);
  const location = useLocation();
  usePageMeta(
    "About Feis.link: Built by a Feis Organiser",
    "Feis.link is an Irish dance feis platform started by organiser and adjudicator Devin Cornell, ADCRG. Running feiseanna since 2019.",
  );
  const animate = useSiteMotion(root);
  useHashScroll(location);
  const events = signedIn() ? "/" : "/#events";

  return (
    <div className={`site${animate ? " js" : ""}`} ref={root}>
      <SiteHeader active="about" />
      <main>
        <section className="hero org-hero">
          <div className="wrap">
            <span className="eyebrow rv">About Feis.link</span>
            <h1>
              <span className="w logo-word" style={{ "--i": 0 }}><img src={feisLogo} alt="Feis.link" width="107" height="36" /></span>
              <span className="w a" style={{ "--i": 0 }}>,</span>
              {HEADLINE.map(([w, c], i) => (
                <React.Fragment key={w}>
                  {" "}
                  <span className={`w${c ? ` ${c}` : ""}`} style={{ "--i": i + 1 }}>{w}</span>
                </React.Fragment>
              ))}
              <span className="w a" style={{ "--i": HEADLINE.length + 1 }}>.</span>
            </h1>
            <p className="lede rv">Feis.link runs registration, payments, tabulation and results for Irish dance feiseanna, grade exams and the Oireachtas. It was built by a feis organiser, and it has run feis day since 2019.</p>
            <div className="hero-acts rv">
              <Link className="sbtn big" to={events}>Find an event</Link>
              <Link className="sbtn big ghost" to="/organisers">For organisers</Link>
            </div>
          </div>
        </section>

        <section className="join" aria-labelledby="founder-h">
          <div className="wrap">
            <div className="founder rv"><img src={founder} alt="Devin Cornell with his dancers in team costumes" width="1600" height="762" /></div>
            <div className="panel made-panel rv">
              <h2 id="founder-h">Devin Cornell, ADCRG</h2>
              <p>Feis.link was started by Devin Cornell, director of the Cornell School of Irish Dance in Chatham, Ontario. Devin has organised feiseanna for more than ten years, adjudicates, and is a dance parent: he has seen feis day from the committee table, the judges' table and the side of the stage.</p>
              <p>He built Feis.link to be the system he wanted on feis day: one place for the entries, the money, the marks and the results, that a feis committee can run themselves.</p>
              <p className="who"><b>Devin Cornell, ADCRG</b> · Founder · <a className="inline" href="mailto:info@feis.link">info@feis.link</a></p>
            </div>
          </div>
        </section>

        <section className="wrap org-sec" aria-labelledby="believe-h">
          <div className="sec-head rv"><div><h2 id="believe-h">What we believe</h2><p>Three things every decision comes back to.</p></div></div>
          <div className="sup-list">
            {BELIEFS.map(([title, text]) => (
              <article className="sup glow rv" key={title}><h3>{title}</h3><p>{text}</p></article>
            ))}
          </div>
        </section>

        <section className="wrap org-sec" id="contact" aria-labelledby="contact-h">
          <div className="sec-head rv"><div><h2 id="contact-h">Contact</h2><p>Questions from families, teachers and organisers all come to the same inbox.</p></div></div>
          <div className="contact rv">
            <div><span className="eyebrow">Email</span><p><a className="inline big soc-link" href="mailto:info@feis.link"><EmailIcon />info@feis.link</a></p></div>
            <div><span className="eyebrow">Mail</span><p className="soc-addr"><MailboxIcon /><span>Feis.link<br />21432 Lynn Rd.<br />Wheatley, Ontario N0P 2P0<br />Canada</span></p></div>
            <div>
              <span className="eyebrow">Follow</span>
              <p className="socials">
                <a className="inline soc-link" href="https://www.instagram.com/feis.link" target="_blank" rel="noopener noreferrer"><InstagramIcon />@feis.link</a>
                <a className="inline soc-link" href="https://www.facebook.com/feis.link" target="_blank" rel="noopener noreferrer"><FacebookIcon />facebook.com/feis.link</a>
              </p>
            </div>
          </div>
          <div className="paths">
            <Link className="path glow rv" to={events}><span className="eyebrow">For families</span><b>Find an event and register</b><span className="go">Upcoming events ›</span></Link>
            <Link className="path glow rv" to="/organisers"><span className="eyebrow">For organisers</span><b>Run your feis on Feis.link</b><span className="go">For organisers ›</span></Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default About;
